(() => {
  if (customElements.get('opd-product-facts')) return;

  class OpdProductFacts extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('.product-section');
      this.row = this.querySelector('[data-opd-dimensions]');
      this.value = this.querySelector('[data-opd-dimensions-value]');
      if (!this.container || !this.row || !this.value) return;
      this.records = new Map();
      this.querySelector('[data-opd-dimension-records]')?.content.querySelectorAll('[data-variant-id]').forEach(record => {
        this.records.set(record.dataset.variantId, record.textContent.trim());
      });
      this.hasFacts = Boolean(this.querySelector('.opd-fact'));
      this.variantEventSerial = 0;
      this.onVariantChange = event => {
        if (event.target !== this.container) return;
        this.variantEventSerial += 1;
        this.showVariant(event.detail?.variant?.id);
      };
      this.onChange = event => {
        if (event.target.closest('.product-section') !== this.container) return;
        if (event.target.matches('[data-product-select]')) {
          const selected = this.variantFromInputs();
          this.showVariant(event.target.value || (selected?.available === false ? selected.id : null));
          return;
        }
        if (!event.target.matches('[data-variant-input]')) return;
        const serial = this.variantEventSerial;
        this.showVariant(null);
        queueMicrotask(() => {
          if (this.isConnected && this.variantEventSerial === serial) {
            this.showVariant(this.variantFromInputs()?.id);
          }
        });
      };
      this.onPopState = () => this.restoreFromLocation();
      this.onPageShow = event => { if (event.persisted) this.restoreFromLocation(); };
      this.container.addEventListener('variantChange', this.onVariantChange);
      this.container.addEventListener('change', this.onChange, true);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('pageshow', this.onPageShow);
      const params = new URL(window.location.href).searchParams;
      const requested = params.get('variant');
      const initial = this.dataset.initialVariantId;
      this.showVariant(params.has('option_values') || (requested !== null && requested !== initial) ? null : initial);
    }

    disconnectedCallback() {
      this.container?.removeEventListener('variantChange', this.onVariantChange);
      this.container?.removeEventListener('change', this.onChange, true);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    variants() {
      try {
        const data = this.container.querySelector('[data-variant-json]');
        return data ? JSON.parse(data.textContent) : [];
      } catch {
        return [];
      }
    }

    optionInputs() {
      return Array.from(this.container.querySelectorAll('[data-variant-input]')).filter(input => input.closest('.product-section') === this.container);
    }

    variantFromInputs() {
      const inputs = this.optionInputs().filter(input => !['radio', 'checkbox'].includes(input.type) || input.checked);
      if (!inputs.length) return null;
      return this.variants().find(variant => inputs.every(input => variant[input.dataset.index] === input.value));
    }

    showVariant(id) {
      const dimensions = id == null ? '' : this.records.get(String(id)) || '';
      this.value.textContent = dimensions;
      this.row.hidden = !dimensions;
      this.hidden = !this.hasFacts && !dimensions;
      this.dataset.currentVariantId = id == null ? '' : String(id);
    }

    restoreFromLocation() {
      const targetUrl = new URL(window.location.href);
      const productUrl = new URL(this.dataset.productUrl, window.location.origin);
      const containerUrl = new URL(this.container.dataset.productUrl || this.dataset.productUrl, window.location.origin);
      const targetPath = targetUrl.pathname.replace(/\/$/, '');
      const sameProductPath = targetPath === productUrl.pathname.replace(/\/$/, '') || targetPath === containerUrl.pathname.replace(/\/$/, '');
      if (!sameProductPath) {
        this.showVariant(null);
        return;
      }
      if (targetUrl.searchParams.has('option_values')) {
        this.showVariant(null);
        return;
      }
      const id = targetUrl.searchParams.get('variant') ?? this.dataset.defaultVariantId;
      const variant = this.variants().find(item => String(item.id) === id);
      this.showVariant(null);
      if (!variant) return;
      const inputs = this.optionInputs();
      const selectedInputs = [];
      inputs.forEach(input => {
        const selectedValue = variant[input.dataset.index];
        if (selectedValue === undefined) return;
        if (['radio', 'checkbox'].includes(input.type)) {
          input.checked = input.value === selectedValue;
          if (input.checked) selectedInputs.push(input);
        } else {
          input.value = selectedValue;
          if (input.value === selectedValue) selectedInputs.push(input);
        }
      });
      const savedHref = targetUrl.href;
      const savedState = window.history.state;
      selectedInputs.sort((a, b) => Number(a.dataset.index.slice(-1)) - Number(b.dataset.index.slice(-1)));
      const trigger = selectedInputs.at(-1);
      const master = this.container.querySelector('[data-product-select]');
      if (trigger) {
        trigger.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!inputs.length && master) {
        master.value = id;
        master.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (window.location.href !== savedHref) window.history.replaceState(savedState, '', savedHref);
      const actual = inputs.length ? this.variantFromInputs() : variant;
      if (actual && String(actual.id) === id && (!master || String(master.value) === id || (actual.available === false && master.value === ''))) this.showVariant(id);
      else this.showVariant(null);
    }
  }

  customElements.define('opd-product-facts', OpdProductFacts);
})();
